import axios from "axios";
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const adminApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("tc_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Ante un 401 en cualquier llamada (token vencido/inválido) cerramos sesión y
// dejamos que ProtectedRoute redirija al login. El login mismo queda excluido:
// si no, una contraseña incorrecta (401) limpiaría la sesión y redirigiría antes
// de que el formulario pueda mostrar "email o contraseña incorrectos".
let limpiarSesionAdmin = () => {};
export const registrarLimpiadorSesionAdmin = (fn) => { limpiarSesionAdmin = fn; };

adminApi.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const esLogin = (err?.config?.url || "").includes("/auth/login");
    if (status === 401 && !esLogin) limpiarSesionAdmin();
    return Promise.reject(err);
  },
);

// Convierte el mensaje de error de FastAPI (string | array | objeto) a texto plano.
export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Ocurrió un error inesperado. Intentá de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  return String(detail);
};

const AuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null); // null=checking, false=no auth, obj=auth
  const [errorSesion, setErrorSesion] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem("tc_admin_token");
    setUsuario(false);
    setErrorSesion(false);
  }, []);

  const cargarSesion = useCallback(async () => {
    const token = localStorage.getItem("tc_admin_token");
    if (!token) {
      setUsuario(false);
      return;
    }
    setErrorSesion(false);
    try {
      const { data } = await adminApi.get("/auth/me");
      setUsuario(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("tc_admin_token");
        setUsuario(false);
      } else {
        // Error de red o del servidor: no expulsamos a un operador con token válido.
        setErrorSesion(true);
      }
    }
  }, []);

  useEffect(() => {
    registrarLimpiadorSesionAdmin(logout);
  }, [logout]);

  useEffect(() => {
    cargarSesion();
  }, [cargarSesion]);

  const login = async (email, password) => {
    const { data } = await adminApi.post("/auth/login", { email, password });
    localStorage.setItem("tc_admin_token", data.token);
    setUsuario(data.usuario);
    return data.usuario;
  };

  return (
    <AuthContext.Provider value={{ usuario, errorSesion, reintentarSesion: cargarSesion, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAdminAuth = () => useContext(AuthContext);
